import dayjs from 'dayjs';
import * as _ from 'lodash';
import { TravelTypes } from '../../../../constants';
import analytics from '../../../../utils/analytics';
import cleanseAmenities from '../../../../helpers/cleanseAmenities';

const extractBrochureData = (brochure, roomDescriptions, doCleanseAmenities) => {
  const initialContent = {
    type: 'doc',
    content: [],
  };

  const createTextParagraph = (text, cssClass) => {
    if (!text?.trim()) {
      return {
        type: 'paragraph',
        attrs: { textAlign: 'left', class: cssClass },
        content: [{ type: 'text', text: '\u200B' }], // Zero-width space to prevent empty node
      };
    }

    return {
      type: 'paragraph',
      attrs: { textAlign: 'left', class: cssClass },
      content: [{ type: 'text', text }],
    };
  };

  const createBulletList = (items) => ({
    type: 'bulletList',
    content: items.map((item) => ({
      type: 'listItem',
      content: [createTextParagraph(item)],
    })),
  });

  const createHeading = (level, text, options = {}) => {
    const { style = '', ...restAttrs } = options;
    return {
      type: 'heading',
      attrs: {
        textAlign: 'left',
        level,
        style,
        ...restAttrs,
      },
      content: [{ type: 'text', text }],
    };
  };

  const createImage = (src, alt = '', extraAttrs = {}) => ({
    type: 'image',
    attrs: { src, alt, ...extraAttrs },
  });
  const createTwoColumnTableList = (items = []) => {
    const rows = [];

    for (let i = 0; i < items.length; i += 2) {
      const first = items[i];
      const second = items[i + 1];
      const cells = [];
      cells.push({
        type: 'tableCell',
        attrs: {
          class: 'no-border',
          style: `
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            text-align: left;
            vertical-align: top;
            width: 50%;
          `,
        },
        content: [createTextParagraph(!first || !first.trim() ? first : `• ${first}`)],
      });

      cells.push({
        type: 'tableCell',
        attrs: {
          class: 'no-border',
          'data-noBorder': 'true',
          style: `
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            text-align: left;
            vertical-align: top;
            width: 50%;
          `,
        },
        content: [createTextParagraph(!second || !second.trim() ? second : `• ${second}`)],
      });
      rows.push({
        type: 'tableRow',
        content: cells,
        attrs: {
          class: 'no-border',
          'data-noBorder': 'true',
          style: `
        border: none !important;
        border-collapse: collapse !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      `,
        },
      });
    }

    return {
      type: 'table',
      attrs: {
        class: 'no-border',
        'data-noBorder': 'true',
        style: `
        border: none !important;
        border-collapse: collapse !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
       `,
      },
      content: rows,
    };
  };

  const wrapSegmentContent = (segmentType, segmentId, content, attrs = {}) => ({
    type: 'segmentContainer',
    attrs: {
      segmentType,
      segmentId,
      ...attrs,
    },
    content,
  });

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  };

  const getDetailedFlightData = (flightDetails, currencySign) => {
    const content = [createHeading(2, 'Flight')];

    flightDetails.flightSegments.forEach((segment, index) => {
      content.push(createHeading(3, `Segment ${index + 1}`));
      segment.flights.forEach((flight) => {
        const dep = dayjs(flight.departDateTime);
        const arr = dayjs(flight.arriveDateTime);
        const duration = formatDuration(flight.duration);
        const items = [
          `${flight.carrierFullName} ${flight.flightNumber} · ${flight.cabinClassName || 'Economy'}`,
          `Departs ${dep.format('MMM D, YYYY')} at ${dep.format('h:mm A')} from ${flight.originAirportFullName} (${flight.originAirportCode})`,
          `Arrives ${arr.format('MMM D, YYYY')} at ${arr.format('h:mm A')} at ${flight.destinationAirportFullName} (${flight.destinationAirportCode})`,
          duration ? `Duration ${duration}` : null,
          `Fare ${currencySign}${flight.farePrice} · Refundable: ${flight.isRefundable ? 'Yes' : 'No'} · Exchangeable: ${flight.exchangePolicy?.beforeDeparture?.isExchangeAble ? 'Yes' : 'No'}`,
        ].filter(Boolean);
        content.push(createBulletList(items));
      });
    });

    return content;
  };

  const getDetailedHotelData = (hotel, currencySign, product) => {
    const placeholderImageBase64 = `data:image/svg+xml;base64,${btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200" preserveAspectRatio="xMidYMid slice">
        <rect width="100%" height="100%" fill="#ccc"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#555" font-size="20" font-family="Arial, sans-serif">
          No photo
        </text>
      </svg>`)}`;

    const checkIn = dayjs(hotel.checkInStartTime).format('MMM D');
    const checkOut = dayjs(hotel.checkOutEndTime).format('MMM D');
    const imageSrc = hotel.image || placeholderImageBase64;
    const content = [
      createHeading(2, 'Hotel', { style: 'margin-bottom: 16px;' }),
      createImage(imageSrc, hotel.name || 'No photo', { class: 'brochure-hotel-image' }),
      createHeading(2, hotel.name),
      createTextParagraph(hotel.fullAddress),
      createTextParagraph(`${checkIn} – ${checkOut}`),
      createBulletList(
        [
          ...(hotel.amenities?.length
            ? (doCleanseAmenities ? cleanseAmenities(hotel.amenities) : hotel.amenities)
                .slice(0, 8)
                .map((a) => a.trim())
                .filter(Boolean)
            : []),
          hotel.hasFreeAirportShuttle ? 'Free airport shuttle' : null,
          hotel.starRating ? `${hotel.starRating} stars` : null,
          hotel.hotelChainName || null,
        ].filter(Boolean),
      ),
    ];

    product.rooms.forEach((room) => {
      const improvedDescription = roomDescriptions[product.code]?.description;

      if (improvedDescription && improvedDescription.sections?.length > 0) {
        content.push(
          createHeading(3, improvedDescription.roomTitle || room.category, {
            style: 'margin-top: 24px;',
          }),
        );

        improvedDescription.sections.forEach((section) => {
          if (section?.infos?.length > 0) {
            content.push(createTextParagraph(section.sectionTitle, 'mt-4'));
            content.push(createTwoColumnTableList(section.infos));
          }
        });
      } else {
        content.push(
          createHeading(3, room.category || 'Room', {
            style: 'margin-top: 24px;',
          }),
        );
        const roomBullets = [
          room.desc || room.originalDesc,
          product.amenities?.length ? `Amenities: ${product.amenities.join(', ')}` : null,
          product.roomPrice?.averageNightlyRate != null
            ? `${currencySign}${product.roomPrice.averageNightlyRate.toFixed(2)} per night`
            : null,
          product.isPrepaidRate ? 'Prepaid rate' : null,
          product.nonRefundable ? 'Non-refundable' : 'Refundable',
          product.cancellationPolicy || null,
        ].filter(Boolean);
        if (roomBullets.length) content.push(createBulletList(roomBullets));
      }
    });

    return content;
  };

  const getDetailedCarData = (carDetails, currencySign) => {
    const pickUp = dayjs(carDetails.pickUpDateTime);
    const dropOff = dayjs(carDetails.dropOffDateTime);
    const content = [
      createHeading(2, 'Car rental'),
      createTextParagraph(
        `${carDetails.carDetails.vehicle} · ${carDetails.carDetails.carClass || ''} · ${carDetails.carDetails.seats || ''} seats`
          .replace(/\s·\s·/g, ' ·')
          .trim(),
      ),
      createTextParagraph(
        `Pick-up: ${carDetails.locationInfo.pickUpAddress} — ${pickUp.format('MMM D, h:mm A')}`,
      ),
      createTextParagraph(
        `Drop-off: ${carDetails.locationInfo.dropOffAddress} — ${dropOff.format('MMM D, h:mm A')}`,
      ),
      createTextParagraph(`Total: ${currencySign}${carDetails.priceInfo.priceAllDays}`),
    ];

    return content;
  };

  if (!brochure) return null;

  const { trips } = brochure;
  const keys = Object.keys(trips);

  keys.forEach((key) => {
    const { tripSegments } = trips[key];

    tripSegments.forEach(({ itinerary }) => {
      const currencyCode = itinerary?.cost?.currencyCode || 'USD';
      const { mode } = itinerary;

      switch (mode) {
        case TravelTypes.flight: {
          const segmentContent = getDetailedFlightData(itinerary, currencyCode);
          initialContent.content.push(
            wrapSegmentContent(mode, key, segmentContent, { itinerary, tripId: brochure.id }),
          );
          break;
        }

        case TravelTypes.hotel: {
          const { hotel } = itinerary;

          hotel.products.forEach((product, index) => {
            const hotelWithSingleProduct = {
              ...hotel,
              products: [product],
            };

            const segmentContent = getDetailedHotelData(
              hotelWithSingleProduct,
              currencyCode,
              product,
            );

            initialContent.content.push(
              wrapSegmentContent(mode, `${key}-${index}`, segmentContent, {
                itinerary,
                product,
                tripId: brochure.id,
              }),
            );
          });

          break;
        }

        case TravelTypes.cruise: {
          const cruiseDetails = _.cloneDeep(itinerary.itineraryDetail);
          const cabinCategoryGroups = _.cloneDeep(itinerary.cabinCategories);

          const departurePort = cruiseDetails.itineraryDays?.[0]?.cityName ?? 'N/A';
          const arrivalPort =
            cruiseDetails.itineraryDays?.[cruiseDetails.itineraryDays.length - 1]?.cityName ??
            'N/A';
          const departureDate = cruiseDetails.itineraryDepartDate;
          const arrivalDate = cruiseDetails.itineraryReturnDate;

          const placeholderImageBase64 = `data:image/svg+xml;base64,${btoa(`
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200" preserveAspectRatio="xMidYMid slice">
                <rect width="100%" height="100%" fill="#ccc"/>
                <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#555" font-size="20" font-family="Arial, sans-serif">
                  No photo
                </text>
              </svg>`)}`;

          cabinCategoryGroups.forEach((group, groupIndex) => {
            const selectedRoomIndex = group.roomIndex;

            group.cabinCategories.forEach((category, catIndex) => {
              const {
                cabinCategoryName,
                cabinCategoryDescription,
                cabinCategoryImage,
                rateOptions = [],
              } = category;

              rateOptions.forEach((rateOption, rateIndex) => {
                const segmentId = `${key}-${groupIndex}-${catIndex}-${rateIndex}`;
                const price = rateOption.price ?? 0;
                const imageSrc = cabinCategoryImage || placeholderImageBase64;

                const content = [
                  createHeading(2, cabinCategoryName),
                  {
                    type: 'table',
                    attrs: {
                      class: 'no-border',
                      'data-noBorder': 'true',
                      style: `
                        width: 100%;
                        border-collapse: collapse;
                        margin: 0;
                        padding: 0;
                      `,
                    },
                    content: [
                      {
                        type: 'tableRow',
                        content: [
                          {
                            type: 'tableCell',
                            attrs: {
                              class: 'no-border',
                              'data-noBorder': 'true',
                              style: `
                                width: 33.33%;
                                padding-right: 12px;
                                vertical-align: top;
                                border: none;
                              `,
                            },
                            content: [createImage(imageSrc, cabinCategoryName)],
                          },
                          {
                            type: 'tableCell',
                            attrs: {
                              class: 'no-border',
                              'data-noBorder': 'true',
                              style: `
                                width: 66.66%;
                                vertical-align: top;
                                padding: 0;
                                padding-right: 12px;
                                border: none;
                              `,
                            },
                            content: [
                              createHeading(3, 'Overview'),
                              createBulletList(
                                [
                                  cruiseDetails.cruiseLine?.cruiseLineName &&
                                  cruiseDetails.ship?.shipName
                                    ? `${cruiseDetails.cruiseLine.cruiseLineName} — ${
                                        cruiseDetails.ship.shipName
                                      }`
                                    : [
                                        cruiseDetails.cruiseLine?.cruiseLineName,
                                        cruiseDetails.ship?.shipName,
                                      ]
                                        .filter(Boolean)
                                        .join(' · ') || 'Cruise',
                                  `${cruiseDetails.itineraryNumNights} nights`,
                                  `${departurePort} → ${arrivalPort}`,
                                  `${dayjs(departureDate).format('MMM D, YYYY')} – ${dayjs(
                                    arrivalDate,
                                  ).format('MMM D, YYYY')}`,
                                ].filter(Boolean),
                              ),
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  ...(cabinCategoryDescription?.trim()
                    ? [createTextParagraph(cabinCategoryDescription.replace(/<br\s*\/?>/g, '\n'))]
                    : []),
                  createHeading(3, 'Rate'),
                  createBulletList(
                    [
                      `${currencyCode === 'USD' ? '$' : `${currencyCode} `}${price}`,
                      rateOption.rateOptionName,
                      rateOption.rateOptionType || null,
                      rateOption.notes || null,
                    ].filter(Boolean),
                  ),
                ];

                initialContent.content.push(
                  wrapSegmentContent(mode, segmentId, content, {
                    rateOption,
                    itinerary: category,
                    selectedRoomIndex,
                    tripId: brochure.id,
                    itineraryDetail: cruiseDetails,
                  }),
                );
              });
            });
          });

          break;
        }

        default: {
          const segmentContent = getDetailedCarData(itinerary, currencyCode);
          initialContent.content.push(
            wrapSegmentContent(mode, key, segmentContent, { itinerary, tripId: brochure.id }),
          );
        }
      }
    });
  });
  analytics.track('brochureEditor.generatedBrochureContent', {
    brochureId: brochure.id,
    totalSegments: initialContent.content.length,
    tripCount: Object.keys(brochure.trips || {}).length,
  });
  return initialContent;
};

export default extractBrochureData;
